import Markdown from "react-markdown"
import styled from "styled-components"

import {
  Badge,
  BadgeProps,
  colors,
  colorScale,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { parseJSON } from "src/components/GuestInbox/GuestChat/messageUtils"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { JsonTree } from "src/ui/JsonTree/JsonTree"
import { MTooltip } from "src/ui/MTooltip/MTooltip"
import { formatDate, formatTimeAgo } from "src/utils/l10n"

export type MessageProps = {
  id: string
  sentAt: string
  content: string
  source: string
  badges?: {
    color: BadgeProps["color"]
    label: string
    size: BadgeProps["size"]
    border?: boolean
  }[]
  senderType: "self" | "other"
  senderName?: string
}

export function Message({
  sentAt,
  content,
  source,
  badges,
  senderType,
  senderName,
}: MessageProps) {
  const { t } = useTranslate()

  const user = useGetUser()

  // Make it more readable by having any response field as normal text
  // and show the rest of the key-value pairs in a JSON tree
  const parsedJson = parseJSON(content)

  return (
    <Wrapper backgroundColor={senderType === "self" ? "blue" : "gray"}>
      {badges && badges.length > 0 && (
        <Badges>
          {badges.map((badge) => (
            <Badge
              key={badge.label}
              color={badge.color}
              size={badge.size}
              border={badge.border}
            >
              {badge.label}
            </Badge>
          ))}
        </Badges>
      )}

      <StyledMarkdown>
        {parsedJson &&
        typeof parsedJson === "object" &&
        "response" in parsedJson ? (
          <div>
            {typeof parsedJson.response === "string" && (
              <Markdown>{parsedJson.response}</Markdown>
            )}

            <JsonTree json={{ ...parsedJson, response: "☝️" }} padding="0" />
          </div>
        ) : (
          <Markdown>{content}</Markdown>
        )}
      </StyledMarkdown>
      <BottomRow>
        <MTooltip
          title={formatDate({
            date: sentAt,
            clockType: user.clock_type,
          })}
        >
          <Text variant="bodyXS" color="secondary">
            {formatTimeAgo({ date: sentAt, locale: "en", style: "narrow", t })}
          </Text>
        </MTooltip>
        <Text variant="bodyXS" color="secondary">
          &bull;
        </Text>
        <Text variant="bodyXS" color="secondary">
          {source}
        </Text>
        {senderName && (
          <>
            <Text variant="bodyXS" color="secondary">
              &bull;
            </Text>
            <Text variant="bodyXS" color="secondary">
              {senderName}
            </Text>
          </>
        )}
      </BottomRow>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ backgroundColor: "gray" | "blue" }>`
  background-color: ${({ backgroundColor }) =>
    backgroundColor === "blue"
      ? colors.accentPrimaryLight
      : colorScale.blueContrast[100]};
  padding: ${spacing.M};
  border-radius: 1rem;
`

const Badges = styled.div`
  display: flex;
  gap: ${spacing.XS2};
  margin-bottom: ${spacing.XS};
`

const BottomRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.XS2};
  margin-top: ${spacing.XS};
`

const StyledMarkdown = styled.div`
  & p:first-child {
    margin-top: 0;
  }

  & p:last-child {
    margin-bottom: 0;
  }
`
